import { Injectable } from '@angular/core';
import * as Forge from 'node-forge';
@Injectable({
  providedIn: 'root',
})
export class EncryptService {
  extractPublicKeyFromCertificate(pemCertificate: string): Forge.pki.PublicKey {
    const certificate = Forge.pki.certificateFromPem(
      pemCertificate,
      true,
      true
    );

    return certificate.publicKey;
  }

  encrypt(props: { text: string; cert: string }) {
    try {
      const clientKey = Forge.pki.certificateFromPem(props.cert);

      const p7 = Forge.pkcs7.createEnvelopedData();
      p7.addRecipient(clientKey);
      p7.content = Forge.util.createBuffer(props.text, 'utf8');

      // Encrypt the data
      p7.encrypt();

      const derBuffer = Forge.asn1.toDer(p7.toAsn1()).getBytes();
      return Forge.util.encode64(derBuffer);
    } catch (error) {
      console.error(error);
      return '';
    }
  }

  encryptHybridPayload(payload: object, rsaPublicKeyPem: string): {
    encrypted_key: string;
    encrypted_data: string;
  } {
    // 1. Stringify JSON object
    const plaintext = JSON.stringify(payload);
  
    // 2. Generate 256-bit AES key and 12-byte IV
    const aesKey = Forge.random.getBytesSync(32); // 32 bytes = 256-bit key
    const iv = Forge.random.getBytesSync(12);     // 12 bytes = recommended GCM nonce
  
    // 3. AES-GCM encryption
    const cipher = Forge.cipher.createCipher('AES-GCM', aesKey);
    cipher.start({ iv, tagLength: 128 });
    cipher.update(Forge.util.createBuffer(plaintext, 'utf8'));
    cipher.finish();
  
    const ciphertext = cipher.output.getBytes(); // tag is ignored
    const tag = cipher.mode.tag.getBytes();
  
    // 4. Concatenate nonce + ciphertext
    const encryptedBytes = iv + ciphertext + tag;
  
    // 5. Base64 encode encrypted data
    const encrypted_data = Forge.util.encode64(encryptedBytes);
  
    // 6. Encrypt AES key with RSA PKCS1v15
    const rsaPublicKey = Forge.pki.publicKeyFromPem(rsaPublicKeyPem);
    const encryptedKeyBytes = rsaPublicKey.encrypt(aesKey, 'RSAES-PKCS1-V1_5');
  
    // 7. Base64 encode encrypted AES key
    const encrypted_key = Forge.util.encode64(encryptedKeyBytes);
  
    return {
      encrypted_key,
      encrypted_data
    };
  }

  generateKeyPair() {
    const rsa = Forge.pki.rsa;
    const keyPair = rsa.generateKeyPair({ bits: 2048 });
    const publicKey = Forge.pki.publicKeyToPem(keyPair.publicKey);
    const privateKey = Forge.pki.privateKeyToPem(keyPair.privateKey);

    return {
      publicKey,
      privateKey,
    };
  }

  base64Decode(data: string): string {
    return Forge.util.decode64(data);
  }

  stringToBytes(data: string) {
    // Decode the Base64 string to a binary string
    // Create a new Uint8Array with the length of the binary string
    const bytes = new Uint8Array(data.length);
    // Convert each character to its byte value
    for (let i = 0; i < data.length; i++) {
      bytes[i] = data.charCodeAt(i);
    }
    return bytes;
  }

  base64UrlDecode(str: string): string {
    return str.replaceAll(/-/g, '+').replaceAll(/_/g, '/');
  }
  // Example usage

  decryptDataPlainString(
    key: string,
    iv: string,
    ciphertextBase64: string
  ): string {
    try {
      // Decode the key, IV, and ciphertext from Base64

      const ciphertext = this.base64Decode(
        this.base64UrlDecode(ciphertextBase64)
      );

      const decipher = Forge.cipher.createDecipher('AES-CBC', key);
      decipher.start({ iv: iv });
      const ciphertextBytes = this.stringToBytes(ciphertext);
      decipher.update(Forge.util.createBuffer(ciphertextBytes));
      const result = decipher.finish(); // Check result to see if decryption was successful

      if (!result) {
        throw new Error('Error finishing decryption');
      }
      const plainText = decipher.output.data;

      return plainText;
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Decryption failed');
    }
  }

  generateAESKeyAndIV(keySize = 16) {
    // Default is 128 bits, 16 bytes
    // Generate a random key
    const key = Forge.random.getBytesSync(keySize);

    // Generate a random IV (Initialization Vector), AES block size is 16 bytes
    const iv = Forge.random.getBytesSync(16);

    // Encode key and IV to a URL-safe base64 format to be easily transported
    const keyBase64 = Forge.util.encode64(key);
    const ivBase64 = Forge.util.encode64(iv);

    return { keyBase64, ivBase64 };
  }
}
